/* eslint-disable max-lines */
import { Box, BoxType, FrameDetection } from '@hakimo-ui/hakimo/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { formatTime } from '../common-util';
import {
  getMediaLayout,
  getMediaResolution,
  MediaLayout,
} from '../image-panel/special-zones-overlay-util';
import VideoHighlightBox from '../multi-video-player/VideoHighlightBox';
import { ImageResolution } from '../special-zones/utils';
import { FrameData, MatchedFace, MatchedFaceWithFrame } from '../types';
import { FaceProfileOverlay } from './FaceProfileOverlay';

interface Props {
  frames: Array<FrameData>;
  currentFrameIndex: number;
  timeZone: string;
  width?: number | string;
  height?: number | string;
  className?: string;
  onFrameChange?: (idx: number) => void;
  elaborateFrames?: boolean;
  pauseBeforeNextInSec?: number;
  paused?: boolean;
  showMatchedFaces?: boolean;
  /**
   * Decoded-paint mode: when this prop is passed (even as null), the player
   * paints the given already-decoded image into a persistent <canvas> instead
   * of loading frame.url into an <img>. This guarantees the pixels on screen
   * are the ones the caller decoded — no second URL load that can miss the
   * browser cache, refetch from the network and be cancelled by the next
   * frame (the multi-camera frame-freeze root cause). null = nothing decoded
   * yet (canvas keeps the last painted frame). Omit for the legacy <img> path.
   */
  decodedFrame?: HTMLImageElement | null;
  /**
   * Reports the element the pixels land in (the canvas in decoded-paint mode,
   * the <img> otherwise) on mount, identity change and unmount — so parents
   * can anchor their own overlays to it without reaching into this
   * component's DOM.
   */
  onMediaElement?: (el: HTMLCanvasElement | HTMLImageElement | null) => void;
}

function convertMatchedFacesToBoxes(matchedFaces: MatchedFace[]): Box[] {
  return matchedFaces
    .filter((face) => Array.isArray(face.face_box) && face.face_box.length >= 4)
    .map((face) => ({
      frame: -1,
      coords: face.face_box,
      type: BoxType.FACE,
    }));
}

/**
 * Detection boxes (persons/vehicles) come as {x1,y1,x2,y2} in the frame's
 * pixel space; Box.coords is [left, top, width, height] in that same space
 * (see getVideoPolygons). Malformed boxes are dropped rather than drawn wrong.
 */
export function convertDetectionsToBoxes(
  detections: FrameDetection[] | undefined,
  type: BoxType
): Box[] {
  return (detections ?? [])
    .filter(
      (d) =>
        d.box &&
        [d.box.x1, d.box.y1, d.box.x2, d.box.y2].every(Number.isFinite) &&
        d.box.x2 > d.box.x1 &&
        d.box.y2 > d.box.y1
    )
    .map((d) => ({
      frame: -1,
      coords: [d.box.x1, d.box.y1, d.box.x2 - d.box.x1, d.box.y2 - d.box.y1],
      type,
    }));
}

/**
 * All overlay boxes for one frame. Person/vehicle detections always render;
 * face boxes are part of the face-matching feature, so they follow its flag.
 */
function getFrameBoxes(frame: FrameData, includeFaces: boolean): Box[] {
  return [
    ...(includeFaces
      ? convertMatchedFacesToBoxes(frame.matched_faces ?? [])
      : []),
    ...convertDetectionsToBoxes(frame.persons, BoxType.PERSON),
    ...convertDetectionsToBoxes(frame.vehicles, BoxType.VEHICLE),
  ];
}

export function FramePlayer({
  frames,
  currentFrameIndex,
  timeZone,
  width = '100%',
  height = '100%',
  className = '',
  onFrameChange,
  elaborateFrames,
  pauseBeforeNextInSec = 0.3,
  paused = false,
  showMatchedFaces = false,
  decodedFrame,
  onMediaElement,
}: Props) {
  const paintDecoded = decodedFrame !== undefined;
  const frameImageRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const attachCanvas = useCallback(
    (el: HTMLCanvasElement | null) => {
      canvasRef.current = el;
      onMediaElement?.(el);
    },
    [onMediaElement]
  );
  const attachImage = useCallback(
    (el: HTMLImageElement | null) => {
      frameImageRef.current = el;
      onMediaElement?.(el);
    },
    [onMediaElement]
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRootRef = useRef<HTMLDivElement>(null);

  const [layout, setLayout] = useState<MediaLayout | null>(null);
  const [imageResolution, setImageResolution] =
    useState<ImageResolution | null>(null);

  const frame = useMemo(
    () => (frames.length > 0 ? frames[currentFrameIndex] : undefined),
    [currentFrameIndex, frames]
  );

  // Overlay boxes for the frame on screen: matched faces + person/vehicle
  // detections, rendered through the same pipeline as video bounding boxes.
  const frameBoxes = useMemo<Box[]>(
    () => (frame ? getFrameBoxes(frame, showMatchedFaces) : []),
    [frame, showMatchedFaces]
  );

  const allMatchedFaces = useMemo<MatchedFaceWithFrame[]>(() => {
    if (!showMatchedFaces) return [];

    // Scope faces to the alarm being played. The unified alarm group merges a
    // camera's frames from many alarms into one track; without this, one alarm's
    // detected face would show across every alarm. Single-alarm callers leave
    // alarmId undefined, so this filter is a no-op for them.
    const currentAlarmId = frame?.alarmId;
    const facesMap = new Map<string, MatchedFaceWithFrame>();
    frames.forEach((f) => {
      if (f.alarmId !== currentAlarmId) return;
      f.matched_faces?.forEach((face) => {
        if (!facesMap.has(face.profile_id)) {
          facesMap.set(face.profile_id, {
            ...face,
            frame_url: f.url,
            timestamp: f.timestamp,
            cameraName: f.cameraName,
            alarmType: f.alarmType,
          });
        }
      });
    });

    return Array.from(facesMap.values());
  }, [frames, showMatchedFaces, frame?.alarmId]);

  const moveToNextFrame = useCallback(() => {
    if (paused) return;
    // Clear any existing timer to prevent duplicates
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    let nextFrame = currentFrameIndex + 1;
    if (nextFrame >= frames.length) {
      nextFrame = 0;
    }
    timeoutRef.current = setTimeout(() => {
      onFrameChange?.(nextFrame);
    }, pauseBeforeNextInSec * 1000);
  }, [
    currentFrameIndex,
    frames.length,
    onFrameChange,
    pauseBeforeNextInSec,
    paused,
  ]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (frameImageRef.current?.complete) {
      moveToNextFrame();
    }
  }, [frameImageRef.current?.complete, moveToNextFrame]);

  // The element overlays anchor to: the canvas in decoded-paint mode, the
  // <img> otherwise.
  const getMediaEl = useCallback(
    () => (paintDecoded ? canvasRef.current : frameImageRef.current),
    [paintDecoded]
  );

  const updateOverlay = useCallback(() => {
    const mediaElement = getMediaEl();
    const overlayRoot = overlayRootRef.current;
    if (!mediaElement || !overlayRoot) return;
    const rect = getMediaLayout(mediaElement, overlayRoot);
    if (rect) setLayout(rect);
    const res = getMediaResolution(mediaElement);
    if (res) setImageResolution(res);
  }, [getMediaEl]);

  // Decoded-paint mode: draw the decoded bitmap synchronously. drawImage from
  // a decoded HTMLImageElement cannot hit the network, cannot be cancelled by
  // the next frame, and needs no load event — the paint is unconditional.
  useEffect(() => {
    if (!paintDecoded || !decodedFrame) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const w = decodedFrame.naturalWidth;
    const h = decodedFrame.naturalHeight;
    if (!w || !h) return;
    const resized = canvas.width !== w || canvas.height !== h;
    if (resized) {
      canvas.width = w;
      canvas.height = h;
    }
    canvas.getContext('2d')?.drawImage(decodedFrame, 0, 0, w, h);
    if (resized) {
      // Content resolution (re)established — let overlay layers that listen
      // for media 'load' (SpecialZonesOverlay) re-measure.
      canvas.dispatchEvent(new Event('load'));
    }
    updateOverlay();
  }, [paintDecoded, decodedFrame, updateOverlay]);

  const handleImageLoad = useCallback(() => {
    updateOverlay();
    moveToNextFrame();
  }, [updateOverlay, moveToNextFrame]);

  const handleOnError = useCallback(() => {
    setLayout(null);
    setImageResolution(null);
    moveToNextFrame();
  }, [moveToNextFrame]);

  useEffect(() => {
    // Legacy <img> path only: the next URL loads asynchronously, so stale
    // layout must be dropped. The canvas paint is synchronous and updates the
    // overlay itself — resetting here would just flash the overlays off.
    if (paintDecoded) return;
    setLayout(null);
    setImageResolution(null);
  }, [frame?.url, paintDecoded]);

  useEffect(() => {
    // Overlay layout only matters while boxes are on screen.
    if (frameBoxes.length === 0) return;
    const mediaElement = getMediaEl();
    const overlayRoot = overlayRootRef.current;
    const overlayContainer = overlayRoot?.parentElement;
    if (!mediaElement || !overlayRoot || !overlayContainer) return;
    const resizeObserver = new ResizeObserver(updateOverlay);
    resizeObserver.observe(mediaElement);
    resizeObserver.observe(overlayContainer);
    return () => resizeObserver.disconnect();
  }, [frameBoxes, updateOverlay, frame?.url, getMediaEl]);

  return (
    <div
      className={`frame-player relative flex items-center justify-center ${className}`}
      style={{ width, height }}
      ref={containerRef}
    >
      {frame ? (
        <div className="relative h-full w-full">
          {elaborateFrames && (
            <span className="bg-ondark-bg-2 text-ondark-text-1 absolute right-0 top-0 z-20 rounded text-xs">
              {formatTime(new Date(frame.timestamp), timeZone, false)}{' '}
              {timeZone}
            </span>
          )}
          {paintDecoded ? (
            <canvas
              ref={attachCanvas}
              role="img"
              aria-label={`Frame ${currentFrameIndex + 1}`}
              style={{ objectFit: 'contain', width: '100%', height: '100%' }}
            />
          ) : (
            <img
              ref={attachImage}
              src={frame.url}
              alt={`Frame ${currentFrameIndex + 1}`}
              style={{ objectFit: 'contain', width: '100%', height: '100%' }}
              onLoad={handleImageLoad}
              onError={handleOnError}
            />
          )}
          {showMatchedFaces && allMatchedFaces.length > 0 && (
            <FaceProfileOverlay matchedFaces={allMatchedFaces} />
          )}
          <div
            ref={overlayRootRef}
            className="pointer-events-none absolute inset-0"
          >
            {layout && imageResolution && frameBoxes.length > 0 && (
              <div
                className="absolute"
                style={{
                  top: layout.top,
                  left: layout.left,
                  width: layout.width,
                  height: layout.height,
                }}
              >
                <VideoHighlightBox
                  boxes={frameBoxes}
                  videoRect={new DOMRect(0, 0, layout.width, layout.height)}
                  videoWidth={imageResolution.width}
                  videoHeight={imageResolution.height}
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-ondark-text-1 absolute inset-0 flex animate-pulse items-center justify-center text-xs">
          Events loading...
        </p>
      )}
    </div>
  );
}

export default FramePlayer;
